import classNames from 'classnames';
import { DateTime } from 'luxon';
import { numberShorthand } from '@benzinga/utils';
import { BaseQuoteProfile, QuoteFund } from '../../utils/quote/types';
import { useTranslation } from 'react-i18next';
import { SectionTitle } from '@benzinga/core-ui';

interface QuoteMetricRowProps {
  label: string | React.ReactElement;
  value: string | number | React.ReactElement | React.ReactElement[];
  secondValue?: string | number | React.ReactElement;
  className?: string;
  labelClassName?: string | false;
  valueClassName?: string | false;
  secondValueClassName?: string | false;
}

export const QuoteMetricRow = ({
  className,
  label,
  labelClassName,
  secondValue,
  secondValueClassName,
  value,
  valueClassName,
}: QuoteMetricRowProps) => {
  return (
    <tr
      className={classNames('flex w-full py-1.5 text-sm items-center min-h-[40px]', {
        [`${className}`]: !!className,
      })}
    >
      <td
        className={classNames('label text-gray-500 flex-1', {
          [`${labelClassName}`]: !!labelClassName,
        })}
      >
        {label}
      </td>
      {secondValue && (
        <td
          className={classNames('value text-gray-500 flex-1', {
            [`${secondValueClassName}`]: !!secondValueClassName,
          })}
        >
          {secondValue}
        </td>
      )}
      <td
        className={classNames('value font-bold flex-1', {
          [`${valueClassName}`]: !!valueClassName,
          'text-right': !valueClassName?.toString().includes('text-left'),
        })}
      >
        {value}
      </td>
    </tr>
  );
};


export const ETFQuoteMetrics = ({ profile, etfFund }: { profile: BaseQuoteProfile; etfFund: QuoteFund | null }) => {
  const { t } = useTranslation('quote');
  return (
    <div className="card p-4">
      <MainMetrics etfFund={etfFund} profile={profile} />
      {false && <MiddleMetrics profile={profile} />}
    </div>
  );
};

const MainMetrics = ({ profile, etfFund }: { profile: BaseQuoteProfile; etfFund?: QuoteFund | null }) => {
  const { t, i18n } = useTranslation('quote');

  const dividendFrequencyLetter = etfFund?.dividend?.frequency;
  const dividendFrequency =
    dividendFrequencyLetter === 'M'
      ? t('Date.Periods.monthly')
      : dividendFrequencyLetter === 'Q'
        ? t('Date.Periods.quarterly')
        : dividendFrequencyLetter === 'R'
          ? t('Date.Periods.other-regular')
          : dividendFrequencyLetter === 'A'
            ? t('Date.Periods.annual')
            : dividendFrequencyLetter;

  const dividend = etfFund?.dividend?.amount ?? null;
  const dividendYield = etfFund?.dividend?.yield ?? null;

  const exDividendDate = etfFund?.dividend?.date;
  const exDividendJSDate = exDividendDate ? DateTime.fromISO(exDividendDate ?? '') : '';
  const formattedDate = exDividendJSDate ? exDividendJSDate.setLocale(i18n.language).toFormat('MMM d, yyyy') : '';

  const metrics = [
    {
      label: t('Quote.Metrics.open'),
      value: profile?.richQuoteData?.open ? `$${numberShorthand(profile?.richQuoteData.open, 2)}` : '-',
    },
    {
      label: t('Quote.Metrics.high'),
      value: profile?.richQuoteData?.high ? `$${numberShorthand(profile?.richQuoteData.high, 2)}` : '-',
    },
    {
      label: t('Quote.Metrics.52wkhigh'),
      value: profile?.richQuoteData?.fiftyTwoWeekHigh
        ? `$${numberShorthand(profile?.richQuoteData.fiftyTwoWeekHigh, 2) || '-'}`
        : '-',
    },
    {
      label: t('Quote.Metrics.assets-under-management'),
      value: etfFund?.fund_aum ? numberShorthand(etfFund?.fund_aum, 2) : '-',
    },
    { label: t('Quote.Metrics.dividend'), value: dividend ? `$${numberShorthand(dividend, 2)}` : '-' },
    { label: t('Quote.Metrics.ex-div-date'), value: formattedDate ? formattedDate : '-' },
    {
      label: t('Quote.Metrics.volume'),
      value: `${profile?.richQuoteData?.volume ? numberShorthand(profile?.richQuoteData.volume, 2, true) : '-'}`,
    },
    { label: t('Quote.Metrics.market-cap'), value: etfFund?.marketcapital ? etfFund.marketcapital : '-' },
    { label: t('Quote.Metrics.management-fee'), value: etfFund?.management_fee ? `${etfFund?.management_fee}%` : '-' },
    {
      label: t('Quote.Metrics.pe-ratio'),
      value: etfFund?.price_to_earning ? numberShorthand(etfFund.price_to_earning, 2) : '-',
    },
    {
      label: t('Quote.Metrics.previous-close'),
      value: profile?.richQuoteData?.previousClosePrice
        ? `$${numberShorthand(profile?.richQuoteData.previousClosePrice, 2)}`
        : '-',
    },
    {
      label: t('Quote.Metrics.low'),
      value: profile?.richQuoteData?.low ? `$${numberShorthand(profile?.richQuoteData.low, 2)}` : '-',
    },
    {
      label: t('Quote.Metrics.52wklow'),
      value: profile?.richQuoteData?.fiftyTwoWeekLow
        ? `$${numberShorthand(profile?.richQuoteData.fiftyTwoWeekLow, 2) || '-'}`
        : '-',
    },
    {
      label: t('Quote.Metrics.shares-out'),
      value: etfFund?.shares_outstanding ? numberShorthand(etfFund.shares_outstanding, 2) : '-',
    },
    { label: t('Quote.Metrics.yield'), value: dividendYield ? `${numberShorthand(Number(dividendYield), 2)}%` : '-' },
    { label: t('Quote.Metrics.div-freq'), value: dividendFrequency || '-' },
    {
      label: t('Quote.Metrics.average-daily-volume'),
      value: etfFund?.avg_vol_30days ? numberShorthand(etfFund.avg_vol_30days, 2) : '-',
    },
    { label: t('Quote.Metrics.beta'), value: etfFund?.beta ? numberShorthand(etfFund.beta, 2) : '-' },
    { label: t('Quote.Metrics.average-expense-ratio'), value: etfFund?.avg_expense_ratio || '-' },
    {
      label: t('Quote.Metrics.pb-ratio'),
      value: etfFund?.price_to_book ? numberShorthand(etfFund.price_to_book, 2) : '-',
    },
  ];

  return (
    <>
      <SectionTitle level={2} className="mb-2">
        {t('Quote.Metrics.key-statistics')}
      </SectionTitle>
      {metrics.map(metric => (
        <div
          className="flex w-full justify-between py-1.5 text-sm items-center border-b border-bzblue-300 last:border-b-0"
          key={metric.label}
        >
          <div className="label text-gray-500">{metric.label}</div>
          <div className="value font-bold text-right">{metric.value}</div>
        </div>
      ))}
    </>
  );
};

// eslint-disable-next-line no-empty-pattern
const MiddleMetrics = ({}: { profile?: BaseQuoteProfile }) => {
  const { t } = useTranslation('quote');

  return (
    <table className="w-full h-full flex gap-x-4 px-4 py-2 rounded border border-bzblue-400">
      <tbody className="flex flex-col h-full w-full divide-y divide-bzblue-300">
        <QuoteMetricRow label={t('Quote.Metrics.implied-volatility')} value={'-'} />
        <QuoteMetricRow label={t('Quote.Metrics.put-call-volume-ratio')} value={'-'} />
      </tbody>
    </table>
  );
};

export default ETFQuoteMetrics;
